import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type Deal, createDeal, fetchContacts, fetchDeals, updateDeal } from '../api/resources';
import { defaultStages } from '../constants/pipelineStages';

export function DealsPage() {
  const { data: contacts } = useQuery({ queryKey: ['contacts'], queryFn: fetchContacts });
  const { data: deals } = useQuery({ queryKey: ['deals'], queryFn: fetchDeals });
  const queryClient = useQueryClient();

  const [formState, setFormState] = useState({
    contactId: '',
    stageId: defaultStages[0]?.id ?? 'stage-new',
    name: '',
    value: 0,
    probability: 50,
    closeDate: '',
  });
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [editState, setEditState] = useState({
    name: '',
    stageId: defaultStages[0]?.id ?? 'stage-new',
    value: 0,
    probability: 50,
    closeDate: '',
  });

  const createMutation = useMutation({
    mutationFn: createDeal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      setFormState({ contactId: '', stageId: defaultStages[0]?.id ?? 'stage-new', name: '', value: 0, probability: 50, closeDate: '' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: {
        name: string;
        stageId: string;
        value: number;
        probability: number;
        closeDate: number | null;
      };
    }) => updateDeal(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      setEditingDeal(null);
    },
  });

  const openEditor = (deal: Deal) => {
    setEditingDeal(deal);
    setEditState({
      name: deal.name,
      stageId: deal.stage_id,
      value: deal.value,
      probability: deal.probability,
      closeDate: deal.close_date ? new Date(deal.close_date * 1000).toISOString().slice(0, 10) : '',
    });
  };

  const closeEditor = () => setEditingDeal(null);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Deals</h1>
          <p>Track stages, forecast, and close with clarity.</p>
        </div>
        <div className="page-header-actions">
          <Link className="btn btn--secondary" to="/import?type=deals">
            Import CSV
          </Link>
        </div>
      </div>

      <div className="card">
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!formState.contactId || !formState.name) return;
            createMutation.mutate({
              contactId: formState.contactId,
              stageId: formState.stageId,
              name: formState.name,
              value: Number(formState.value),
              probability: Number(formState.probability),
              closeDate: formState.closeDate ? Math.floor(new Date(formState.closeDate).getTime() / 1000) : undefined,
            });
          }}
        >
          <div className="form-grid">
            <label>
              Deal name
              <input value={formState.name} onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))} required />
            </label>
            <label>
              Contact
              <select value={formState.contactId} onChange={(e) => setFormState((s) => ({ ...s, contactId: e.target.value }))} required>
                <option value="">Select contact…</option>
                {contacts?.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {[contact.first_name, contact.last_name].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Stage
              <select value={formState.stageId} onChange={(e) => setFormState((s) => ({ ...s, stageId: e.target.value }))}>
                {defaultStages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-grid">
            <label>
              Value (USD)
              <input type="number" value={formState.value} onChange={(e) => setFormState((s) => ({ ...s, value: Number(e.target.value) }))} />
            </label>
            <label>
              Probability (%)
              <input type="number" min={0} max={100} value={formState.probability} onChange={(e) => setFormState((s) => ({ ...s, probability: Number(e.target.value) }))} />
            </label>
            <label>
              Expected close
              <input type="date" value={formState.closeDate} onChange={(e) => setFormState((s) => ({ ...s, closeDate: e.target.value }))} />
            </label>
          </div>
          <div className="form-actions">
            <button className="btn btn--primary" type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Saving…' : 'Create deal'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Deal</th>
              <th>Stage</th>
              <th>Value</th>
              <th>Probability</th>
              <th>Contact</th>
            </tr>
          </thead>
          <tbody>
            {deals?.map((deal) => (
              <tr key={deal.id} className="table-row" onClick={() => openEditor(deal)}>
                <td>{deal.name}</td>
                <td>{deal.stage_name}</td>
                <td>${deal.value.toLocaleString()}</td>
                <td>{deal.probability}%</td>
                <td>{[deal.first_name, deal.last_name].filter(Boolean).join(' ')}</td>
              </tr>
            ))}
            {!deals?.length && (
              <tr>
                <td colSpan={5}>No deals recorded.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingDeal && (
        <div className="modal-overlay" onClick={closeEditor}>
          <div
            className="modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h2>Edit deal</h2>
            <p className="modal-description">Adjust stage, value, and close dates without leaving the pipeline.</p>
            <p className="modal-meta">Contact: {[editingDeal.first_name, editingDeal.last_name].filter(Boolean).join(' ')}</p>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                const closeDateSeconds = editState.closeDate
                  ? Math.floor(new Date(editState.closeDate).getTime() / 1000)
                  : null;
                updateMutation.mutate({
                  id: editingDeal.id,
                  payload: {
                    name: editState.name,
                    stageId: editState.stageId,
                    value: Number.isNaN(editState.value) ? 0 : editState.value,
                    probability: Number.isNaN(editState.probability) ? 0 : editState.probability,
                    closeDate: closeDateSeconds,
                  },
                });
              }}
            >
              <label>
                Deal name
                <input value={editState.name} onChange={(e) => setEditState((s) => ({ ...s, name: e.target.value }))} required />
              </label>
              <div className="form-grid">
                <label>
                  Stage
                  <select value={editState.stageId} onChange={(e) => setEditState((s) => ({ ...s, stageId: e.target.value }))}>
                    {defaultStages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Value (USD)
                  <input
                    type="number"
                    value={editState.value}
                    onChange={(e) => setEditState((s) => ({ ...s, value: Number(e.target.value) }))}
                  />
                </label>
                <label>
                  Probability (%)
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={editState.probability}
                    onChange={(e) => setEditState((s) => ({ ...s, probability: Number(e.target.value) }))}
                  />
                </label>
              </div>
              <label>
                Expected close
                <input type="date" value={editState.closeDate} onChange={(e) => setEditState((s) => ({ ...s, closeDate: e.target.value }))} />
              </label>
              <div className="form-actions">
                <button className="btn" type="button" onClick={closeEditor}>
                  Cancel
                </button>
                <button className="btn btn--primary" type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
